/**
 * Global Keyboard Navigation Hook
 * "Power users should never touch the mouse"
 */

import { useEffect, useCallback, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate } from 'react-router-dom';

// Global shortcut definitions
export const GLOBAL_SHORTCUTS = {
  // Navigation - 'g' prefix (go to)
  'g h': { action: 'navigate', path: '/', description: 'Go to Home' },
  'g m': { action: 'navigate', path: '/matters', description: 'Go to Matters' },
  'g c': { action: 'navigate', path: '/clients', description: 'Go to Clients' },
  'g d': { action: 'navigate', path: '/documents', description: 'Go to Documents' },
  'g b': { action: 'navigate', path: '/billing', description: 'Go to Billing' },
  'g s': { action: 'navigate', path: '/settings', description: 'Go to Settings' },
  'g p': { action: 'navigate', path: '/profile', description: 'Go to Profile' },

  // Actions
  '?': { action: 'command', command: 'openHelp', description: 'Open keyboard shortcuts' },
  '/': { action: 'command', command: 'openSearch', description: 'Global search' },
  'mod+k': { action: 'command', command: 'openCommandPalette', description: 'Command palette' },
  'n': { action: 'command', command: 'newItem', description: 'New (context-aware)' },
  'e': { action: 'command', command: 'editCurrent', description: 'Edit current item' },
  'Escape': { action: 'command', command: 'close', description: 'Close/Cancel/Back' },

  // Decision Stack
  'ArrowRight': { action: 'decision', command: 'approve', description: 'Approve/Accept' },
  'ArrowLeft': { action: 'decision', command: 'reject', description: 'Reject/Snooze' },
  'Space': { action: 'decision', command: 'expand', description: 'Expand details' },
  'Enter': { action: 'decision', command: 'confirm', description: 'Confirm action' },
  'mod+z': { action: 'decision', command: 'undo', description: 'Undo last action' },

  // Audio Player
  'p': { action: 'audio', command: 'playPause', description: 'Play/Pause briefing' },
  's': { action: 'audio', command: 'skip', description: 'Skip to next segment' },
  'v': { action: 'audio', command: 'voiceMode', description: 'Toggle voice mode' },
  'z': { action: 'audio', command: 'zenMode', description: 'Toggle zen mode' },
} as const;

export type ShortcutKey = keyof typeof GLOBAL_SHORTCUTS;
export type ShortcutAction = (typeof GLOBAL_SHORTCUTS)[ShortcutKey];

interface KeyboardNavigationOptions {
  /** Whether keyboard nav is enabled */
  enabled?: boolean;
  /** Callback for command palette */
  onCommandPalette?: () => void;
  /** Callback for search */
  onSearch?: () => void;
  /** Callback for help */
  onHelp?: () => void;
  /** Callback for new item */
  onNew?: () => void;
  /** Callback for edit */
  onEdit?: () => void;
  /** Callback for close/escape */
  onClose?: () => void;
  /** Decision stack callbacks */
  onDecisionApprove?: () => void;
  onDecisionReject?: () => void;
  onDecisionExpand?: () => void;
  onDecisionConfirm?: () => void;
  onDecisionUndo?: () => void;
  /** Audio callbacks */
  onAudioPlayPause?: () => void;
  onAudioSkip?: () => void;
  onAudioVoiceMode?: () => void;
  onAudioZenMode?: () => void;
}

export function useKeyboardNavigation(options: KeyboardNavigationOptions = {}) {
  const navigate = useNavigate();
  const {
    enabled = true,
    onCommandPalette,
    onSearch,
    onHelp,
    onNew,
    onEdit,
    onClose,
    onDecisionApprove,
    onDecisionReject,
    onDecisionExpand,
    onDecisionConfirm,
    onDecisionUndo,
    onAudioPlayPause,
    onAudioSkip,
    onAudioVoiceMode,
    onAudioZenMode,
  } = options;

  // Track if user is in an input field
  const isInputFocused = useCallback(() => {
    const active = document.activeElement;
    if (!active) return false;
    const tag = active.tagName.toLowerCase();
    return (
      tag === 'input' ||
      tag === 'textarea' ||
      tag === 'select' ||
      (active as HTMLElement).isContentEditable
    );
  }, []);

  // Navigation shortcuts (g + key sequence)
  const gPressedRef = useRef(false);
  const gTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if in input
      if (isInputFocused()) return;

      // Handle 'g' prefix for navigation
      if (e.key === 'g' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        gPressedRef.current = true;
        // Clear after 500ms
        gTimeoutRef.current = setTimeout(() => {
          gPressedRef.current = false;
        }, 500);
        return;
      }

      // Handle navigation after 'g'
      if (gPressedRef.current) {
        gPressedRef.current = false;
        if (gTimeoutRef.current) clearTimeout(gTimeoutRef.current);

        const routes: Record<string, string> = {
          h: '/',
          m: '/matters',
          c: '/clients',
          d: '/documents',
          b: '/billing',
          s: '/settings',
          p: '/profile',
        };

        if (routes[e.key]) {
          e.preventDefault();
          navigate(routes[e.key]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (gTimeoutRef.current) clearTimeout(gTimeoutRef.current);
    };
  }, [enabled, navigate, isInputFocused]);

  // Command palette: Cmd/Ctrl + K
  useHotkeys(
    'mod+k',
    (e) => {
      e.preventDefault();
      onCommandPalette?.();
    },
    { enabled, enableOnFormTags: false }
  );

  // Search: /
  useHotkeys(
    '/',
    (e) => {
      if (isInputFocused()) return;
      e.preventDefault();
      onSearch?.();
    },
    { enabled }
  );

  // Help: ?
  useHotkeys(
    'shift+/',
    (e) => {
      if (isInputFocused()) return;
      e.preventDefault();
      onHelp?.();
    },
    { enabled }
  );

  // New: n
  useHotkeys(
    'n',
    (e) => {
      if (isInputFocused()) return;
      e.preventDefault();
      onNew?.();
    },
    { enabled }
  );

  // Edit: e
  useHotkeys(
    'e',
    (e) => {
      if (isInputFocused()) return;
      e.preventDefault();
      onEdit?.();
    },
    { enabled }
  );

  // Escape
  useHotkeys(
    'Escape',
    () => {
      onClose?.();
    },
    { enabled, enableOnFormTags: true }
  );

  // Decision shortcuts
  useHotkeys(
    'right',
    (e) => {
      if (isInputFocused()) return;
      e.preventDefault();
      onDecisionApprove?.();
    },
    { enabled }
  );

  useHotkeys(
    'left',
    (e) => {
      if (isInputFocused()) return;
      e.preventDefault();
      onDecisionReject?.();
    },
    { enabled }
  );

  useHotkeys(
    'space',
    (e) => {
      if (isInputFocused()) return;
      e.preventDefault();
      onDecisionExpand?.();
    },
    { enabled }
  );

  useHotkeys(
    'enter',
    (e) => {
      if (isInputFocused()) return;
      e.preventDefault();
      onDecisionConfirm?.();
    },
    { enabled }
  );

  useHotkeys(
    'mod+z',
    (e) => {
      if (isInputFocused()) return;
      e.preventDefault();
      onDecisionUndo?.();
    },
    { enabled }
  );

  // Audio shortcuts
  useHotkeys(
    'p',
    (e) => {
      if (isInputFocused()) return;
      e.preventDefault();
      onAudioPlayPause?.();
    },
    { enabled }
  );

  useHotkeys(
    's',
    (e) => {
      if (isInputFocused()) return;
      e.preventDefault();
      onAudioSkip?.();
    },
    { enabled }
  );

  useHotkeys(
    'v',
    (e) => {
      if (isInputFocused()) return;
      e.preventDefault();
      onAudioVoiceMode?.();
    },
    { enabled }
  );

  useHotkeys(
    'z',
    (e) => {
      if (isInputFocused()) return;
      e.preventDefault();
      onAudioZenMode?.();
    },
    { enabled }
  );

  return {
    shortcuts: GLOBAL_SHORTCUTS,
    isInputFocused,
  };
}

/**
 * Get display string for a keyboard shortcut
 */
export function formatShortcut(key: string): string {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  return key
    .replace('mod', isMac ? '⌘' : 'Ctrl')
    .replace('shift', '⇧')
    .replace('alt', isMac ? '⌥' : 'Alt')
    .replace('ctrl', isMac ? '⌃' : 'Ctrl')
    .replace('ArrowRight', '→')
    .replace('ArrowLeft', '←')
    .replace('ArrowUp', '↑')
    .replace('ArrowDown', '↓')
    .replace('Escape', 'Esc')
    .replace('Space', '␣')
    .replace('Enter', '↵')
    .split('+')
    .map((k) => k.trim())
    .join(' ');
}

export default useKeyboardNavigation;
